/**
 * Fiche d'évaluation élève — Session circulation CCS 2 Roues
 *
 * Route : /enseignant/eval-circulation
 *
 * 10 sections officielles + signatures · Durée max 1h (60 min)
 * Stockage : localStorage clé `c2r:eval-circulation:draft`
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Bike, ClipboardCheck, Activity, Target, ListChecks,
  TrendingUp, AlertTriangle, PenSquare, Compass, Award,
  Printer, Save, Trash2, Timer,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  TouchCheckbox, TouchOuiNon, TouchTriple, EvalSection, APPREHENSION_OPTS,
  FicheCoursLink,
} from '../components/teacher/EvalFormControls';
import SuggestionsBox from '../components/teacher/SuggestionsBox';
import LivretValidationsRecap from '../components/teacher/LivretValidationsRecap';
import { inferFromCirculation, getSousCompetencesLivret } from '../data/teacher/evalInference';
import useSeo from '../hooks/useSeo';

const STORAGE_KEY = 'c2r:eval-circulation:draft';
const EMPTY = {
  nomEleve: '', date: '', enseignant: '', motoUtilisee: '',
  // 1
  // 2A
  kmEffectues: '', habitudes: [], competencesAbordees: [],
  // 2B
  competencesObservees: {},
  // 2C
  typeItineraire: [],
  // 2D
  apprehension: {},
  // 2E
  observationsEnseignant: '',
  // 3
  parcoursTravailles: [],
  // 4
  objectif: '',
  // 5
  criteresRealises: {},
  // 6
  pointsForts: '', pointsAAmeliorer: '',
  // 7
  incidents: {}, commentairesIncidents: '',
  // 8
  ressentiEleve: {}, ressentiPersonnel: '',
  // 9
  travailProchaineSeance: '', conseilsEnseignant: '',
  // 10
  bilanPointsAcquis: '', bilanPointsAAmeliorer: '', bilanTravailPrevu: '',
  // 11. Validations REMC séance (trace pédagogique)
  livretValidations: {},
};

const HABITUDES = ['Quotidienne', 'Occasionnelle', 'Loisirs', 'Travail'];

/**
 * Compétences travaillables en Circulation = toute C2 + C3 + C4 du livret
 * REMC officiel (26 sous-compétences). La C1 (maniement) reste exclusivement
 * Plateau.
 */
const COMPETENCES_CIRCULATION = getSousCompetencesLivret({
  categories: ['C2', 'C3', 'C4'],
});

/** Groupe les sous-compétences par catégorie REMC pour l'affichage. */
const COMP_CIRC_BY_CAT = COMPETENCES_CIRCULATION.reduce((acc, sc) => {
  (acc[sc.categorie] = acc[sc.categorie] || []).push(sc);
  return acc;
}, {});

const CAT_COLOR = { C2: '#7c3aed', C3: '#dc2626', C4: '#16a34a' };
const CAT_LABEL = {
  C2: 'C2 · Circulation normale',
  C3: 'C3 · Conditions difficiles',
  C4: 'C4 · Autonomie',
};

const COMP_ABORDEES = [
  'Allure adaptée', 'Placement', 'Regard', 'Trajectoire', 'Priorités',
  'Franchissements', 'Changement de direction', 'Autonomie itinéraire',
];

const COMP_OBSERVEES_LEGACY = [
  'Connaissance du Code', 'Allure adaptée', 'Observation (regard)',
  'Placement chaussée', 'Espaces de sécurité', 'Anticipation',
  'Prise d\'information', 'Communication usagers', 'Respect priorités/règles',
  'Franchissement intersections', 'Changement direction', 'Insertion / sortie',
  'Gestion dépassements', 'Courtois et respectueux',
  'Autonomie et itinéraire', 'Gestion imprévus',
];

const TYPE_ITINERAIRE = ['En ville', 'Péri-urbain', 'Route', 'Autoroute / voie rapide', 'Mixte'];

const APPREHENSION_LIGNES = [
  { key: 'stress', label: 'Stress / peur chute' },
  { key: 'equilibre', label: 'Gestion équilibre' },
  { key: 'allure', label: 'Gestion allure' },
  { key: 'autres', label: 'Gestion des autres usagers' },
  { key: 'confiance', label: 'Confiance globale' },
];

const PARCOURS_CIRC = [
  'Allure adaptée', 'Placement', 'Trajectoire', 'Priorités',
  'Franchissements', 'Changement de direction', 'Prise d\'informations',
  'Anticipation', 'Communication', 'Autonomie', 'Courtoisie',
];

const CRITERES_CIRC = [
  { key: 'allure', label: 'Allure adaptée' },
  { key: 'placement', label: 'Placement' },
  { key: 'trajectoire', label: 'Trajectoire sécuritaire' },
  { key: 'regard', label: 'Observation – Regard' },
  { key: 'anticipation', label: 'Anticipation' },
  { key: 'respect', label: 'Respect des règles et priorités' },
  { key: 'communication', label: 'Communication' },
  { key: 'autonomie', label: 'Autonomie' },
  { key: 'courtoisie', label: 'Courtoisie' },
];

const INCIDENTS = [
  { key: 'freinageUrgence', label: 'Un freinage d\'urgence' },
  { key: 'changementImprevu', label: 'Un changement de direction imprévu' },
  { key: 'comportementUsager', label: 'Un comportement d\'un autre usager' },
  { key: 'obstacle', label: 'Un obstacle sur la chaussée' },
  { key: 'autre', label: 'Autre situation' },
];

const RESSENTIS = [
  { key: 'comprends', label: "Je comprends l'exercice" },
  { key: 'progression', label: 'Je me sens en progression' },
  { key: 'maitrise', label: 'Je maîtrise mieux la circulation' },
  { key: 'decisions', label: 'Je prends de meilleures décisions' },
  { key: 'aise', label: "Je suis plus à l'aise en circulation" },
];

function loadDraft() {
  try { const raw = localStorage.getItem(STORAGE_KEY); return raw ? { ...EMPTY, ...JSON.parse(raw) } : EMPTY; }
  catch { return EMPTY; }
}

export default function EvalCirculationForm() {
  useSeo({
    title: "Fiche d'évaluation élève · Session circulation CCS 2 Roues",
    description: "Fiche d'évaluation interactive pour les sessions circulation CCS 2 Roues (60 min, 10 sections, sauvegarde locale, impression PDF).",
    path: '/enseignant/eval-circulation',
  });

  const [form, setForm] = useState(() => loadDraft());

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(form)); } catch { /* ignore */ }
  }, [form]);

  const update = (f, v) => setForm((x) => ({ ...x, [f]: v }));
  const toggleArr = (f, v) => setForm((x) => {
    const arr = x[f] || [];
    return { ...x, [f]: arr.includes(v) ? arr.filter((i) => i !== v) : [...arr, v] };
  });
  const updateObj = (f, k, v) => setForm((x) => ({ ...x, [f]: { ...(x[f] || {}), [k]: v } }));

  const handleValidateLivret = (code, status) => {
    setForm((x) => {
      const next = { ...(x.livretValidations || {}) };
      if (status === null || status === undefined) {
        delete next[code];
      } else {
        next[code] = status;
      }
      return { ...x, livretValidations: next };
    });
  };

  const progress = useMemo(() => {
    const filled = [form.nomEleve, form.objectif, form.observationsEnseignant, form.ressentiPersonnel, form.pointsForts, form.travailProchaineSeance].filter((v) => v?.trim()).length;
    return { pct: Math.round((filled / 6) * 100) };
  }, [form]);

  return (
    <div className="space-y-4" data-testid="eval-circulation-page">
      <Link to="/enseignant" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground no-print">
        <ArrowLeft className="w-4 h-4" /> Espace Pro
      </Link>

      {/* HEADER */}
      <header className="relative overflow-hidden rounded-3xl border border-border/60 bg-card" style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 16px 40px -20px rgba(124,58,237,0.25)' }} data-testid="eval-header">
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full blur-3xl opacity-20 pointer-events-none" style={{ background: '#7c3aed' }} aria-hidden="true" />
        <div className="relative p-5 sm:p-7">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-[#7c3aed]/15 border border-[#7c3aed]/30 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-[#7c3aed] mb-2">
            <Bike className="w-3 h-3" /> CCS 2 Roues · Session circulation
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-[1.05]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Fiche d&apos;évaluation élève
          </h1>
          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
            <Timer className="w-3.5 h-3.5" /> Durée max <strong className="text-foreground">1 heure (60 min)</strong>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-black">Nom élève</Label>
              <Input value={form.nomEleve} onChange={(e) => update('nomEleve', e.target.value)} className="mt-1" data-testid="f-nom" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-black">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => update('date', e.target.value)} className="mt-1" data-testid="f-date" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-black">Enseignant</Label>
              <Input value={form.enseignant} onChange={(e) => update('enseignant', e.target.value)} className="mt-1" data-testid="f-enseignant" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider font-black">Moto utilisée</Label>
              <Input value={form.motoUtilisee} onChange={(e) => update('motoUtilisee', e.target.value)} className="mt-1" data-testid="f-moto" />
            </div>
          </div>

          <div className="mt-5 flex items-center gap-3 flex-wrap no-print">
            <div className="flex-1 min-w-[200px] max-w-md">
              <div className="flex items-center justify-between text-[11px] mb-1">
                <span className="font-bold text-muted-foreground">Complétion</span>
                <span className="tabular-nums font-black text-[#7c3aed]">{progress.pct}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-[#7c3aed] transition-all" style={{ width: `${progress.pct}%` }} />
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => { if (window.confirm("Vider la fiche ?")) setForm(EMPTY); }} className="gap-1.5" data-testid="btn-reset">
              <Trash2 className="w-3.5 h-3.5" /> Vider
            </Button>
            <Button size="sm" onClick={() => window.print()} className="gap-1.5 bg-[#7c3aed]" data-testid="btn-print">
              <Printer className="w-4 h-4" /> Imprimer / PDF
            </Button>
          </div>
        </div>
      </header>

      {/* SECTION 1 — PRÉSENTATION – ACCUEIL */}
      <EvalSection n="1" icon={ClipboardCheck} label="Présentation – Accueil" duree="5 min" color="#0891b2" testid="section-1">
        <p className="text-xs text-muted-foreground italic mb-2">Présentation séance · Vérification équipement EPI · Vérification administrative et sécurité · Rappel des objectifs et du déroulement</p>
      </EvalSection>

      {/* SECTION 2 — ÉVALUATION EN CIRCULATION */}
      <EvalSection n="2" icon={Activity} label="Évaluation en circulation" duree="~ 45 min" color="#7c3aed" testid="section-2">
        <div className="space-y-5">
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">A · Nombre de kilomètres déjà effectués</Label>
            <Input value={form.kmEffectues} onChange={(e) => update('kmEffectues', e.target.value)} placeholder="Ex : 150 km" data-testid="f-km" />
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-2 mt-3">Habitudes de conduite</Label>
            <div className="grid grid-cols-2 gap-1.5">
              {HABITUDES.map((h) => (
                <TouchCheckbox key={h} checked={(form.habitudes || []).includes(h)} onChange={() => toggleArr('habitudes', h)} label={h} color="#7c3aed" testid={`hab-${h}`} />
              ))}
            </div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-2 mt-3">Compétences déjà abordées</Label>
            <div className="grid grid-cols-2 gap-1.5">
              {COMP_ABORDEES.map((c) => (
                <TouchCheckbox key={c} checked={(form.competencesAbordees || []).includes(c)} onChange={() => toggleArr('competencesAbordees', c)} label={c} color="#7c3aed" testid={`comp-ab-${c}`} />
              ))}
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">B · Compétences livret observées en circulation · C2 + C3 + C4</Label>
            <p className="text-[11px] italic text-muted-foreground mb-2">26 sous-compétences officielles REMC. Évaluer la capacité de l&apos;élève à circuler en sécurité (Oui = maîtrise, Non = à retravailler). Le badge « Fiche » ouvre le cours à donner.</p>
            <div className="space-y-4">
              {['C2', 'C3', 'C4'].map((catCode) => {
                const items = COMP_CIRC_BY_CAT[catCode];
                if (!items || items.length === 0) return null;
                const color = CAT_COLOR[catCode];
                return (
                  <div key={catCode} data-testid={`circ-cat-${catCode}`}>
                    <div
                      className="flex items-center gap-2 mb-1.5 pb-1 border-b"
                      style={{ borderColor: `${color}55` }}
                    >
                      <span
                        className="inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-white"
                        style={{ background: color }}
                      >
                        {catCode}
                      </span>
                      <span className="text-xs font-bold" style={{ color }}>
                        {CAT_LABEL[catCode]}
                      </span>
                      <span className="text-[10px] text-muted-foreground ml-auto">
                        {items.length} item{items.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      {items.map((sc) => (
                        <TouchOuiNon
                          key={sc.code}
                          label={`${sc.code} · ${sc.intitule}`}
                          value={form.competencesObservees[sc.code]}
                          onChange={(v) => updateObj('competencesObservees', sc.code, v)}
                          testid={`comp-obs-${sc.code}`}
                          extra={<FicheCoursLink code={sc.code} accent={color} />}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-2">C · Type d&apos;itinéraire emprunté</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {TYPE_ITINERAIRE.map((t) => (
                <TouchCheckbox key={t} checked={(form.typeItineraire || []).includes(t)} onChange={() => toggleArr('typeItineraire', t)} label={t} color="#7c3aed" testid={`iti-${t}`} />
              ))}
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-2">D · Niveau d&apos;appréhension ressenti</Label>
            <div className="space-y-1.5">
              {APPREHENSION_LIGNES.map((a) => (
                <TouchTriple key={a.key} label={a.label} value={form.apprehension[a.key]} onChange={(v) => updateObj('apprehension', a.key, v)} options={APPREHENSION_OPTS} testid={`appr-${a.key}`} />
              ))}
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">E · Observations enseignant</Label>
            <Textarea rows={3} value={form.observationsEnseignant} onChange={(e) => update('observationsEnseignant', e.target.value)} data-testid="f-obs" />
          </div>
        </div>
      </EvalSection>

      {/* SECTION 3 — ÉVALUATION COMPÉTENCES EN SITUATION */}
      <EvalSection n="3" icon={Target} label="Évaluation des compétences en situation" color="#1d4ed8" testid="section-3">
        <Label className="text-[10px] uppercase tracking-wider font-black block mb-2">Parcours de circulation (objectifs travaillés)</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
          {PARCOURS_CIRC.map((p) => (
            <TouchCheckbox key={p} checked={(form.parcoursTravailles || []).includes(p)} onChange={() => toggleArr('parcoursTravailles', p)} label={p} color="#1d4ed8" testid={`parc-${p}`} />
          ))}
        </div>
      </EvalSection>

      {/* SECTION 4 — OBJECTIF DE LA SÉANCE */}
      <EvalSection n="4" icon={Target} label="Objectif de la séance" color="#cf2031" testid="section-4">
        <div className="mb-3">
          <SuggestionsBox
            suggestions={inferFromCirculation(form)}
            onAdoptObjectif={(text) => update('objectif', text)}
            onValidateLivret={handleValidateLivret}
            livretValidations={form.livretValidations}
            fichesBaseRoute="/enseignant/terrain"
            accent="#7c3aed"
          />
        </div>
        <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">Objectif formulé avec l&apos;élève (commence par « Savoir »)</Label>
        <Textarea rows={2} value={form.objectif} onChange={(e) => update('objectif', e.target.value)} placeholder="« Savoir... »" className="font-bold" data-testid="f-objectif" />
      </EvalSection>

      {/* SECTION 5 — CRITÈRES DE RÉALISATION */}
      <EvalSection n="5" icon={ListChecks} label="Critères de réalisation" color="#ea580c" testid="section-5">
        <div className="space-y-1.5">
          {CRITERES_CIRC.map((c) => (
            <TouchOuiNon key={c.key} label={c.label} value={form.criteresRealises[c.key]} onChange={(v) => updateObj('criteresRealises', c.key, v)} testid={`crit-${c.key}`} />
          ))}
        </div>
      </EvalSection>

      {/* SECTION 6 — BILAN COMPÉTENCES */}
      <EvalSection n="6" icon={TrendingUp} label="Bilan des compétences en circulation" color="#16a34a" testid="section-6">
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">Points forts observés</Label>
            <Textarea rows={3} value={form.pointsForts} onChange={(e) => update('pointsForts', e.target.value)} data-testid="f-points-forts" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">Points à améliorer</Label>
            <Textarea rows={3} value={form.pointsAAmeliorer} onChange={(e) => update('pointsAAmeliorer', e.target.value)} data-testid="f-points-ameliorer" />
          </div>
        </div>
      </EvalSection>

      {/* SECTION 7 — INCIDENTS / IMPRÉVUS */}
      <EvalSection n="7" icon={AlertTriangle} label="Incidents / Imprévus" color="#f59e0b" testid="section-7">
        <div className="space-y-3">
          <Label className="text-[10px] uppercase tracking-wider font-black block">L&apos;élève a su faire face à :</Label>
          <div className="space-y-1.5">
            {INCIDENTS.map((i) => (
              <TouchOuiNon key={i.key} label={i.label} value={form.incidents[i.key]} onChange={(v) => updateObj('incidents', i.key, v)} testid={`inc-${i.key}`} />
            ))}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">Commentaires</Label>
            <Textarea rows={2} value={form.commentairesIncidents} onChange={(e) => update('commentairesIncidents', e.target.value)} data-testid="f-com-inc" />
          </div>
        </div>
      </EvalSection>

      {/* SECTION 8 — AUTOÉVALUATION ÉLÈVE */}
      <EvalSection n="8" icon={PenSquare} label="Auto-évaluation élève" duree="5 min" color="#16a34a" testid="section-8">
        <div className="space-y-3">
          <Label className="text-[10px] uppercase tracking-wider font-black block">L&apos;élève estime :</Label>
          <div className="space-y-1.5">
            {RESSENTIS.map((r) => (
              <TouchOuiNon key={r.key} label={r.label} value={form.ressentiEleve[r.key]} onChange={(v) => updateObj('ressentiEleve', r.key, v)} testid={`ress-${r.key}`} />
            ))}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">Ressenti personnel</Label>
            <Textarea rows={3} value={form.ressentiPersonnel} onChange={(e) => update('ressentiPersonnel', e.target.value)} placeholder="L'élève s'exprime librement..." data-testid="f-ressenti" />
          </div>
        </div>
      </EvalSection>

      {/* SECTION 9 — MISE EN PERSPECTIVE */}
      <EvalSection n="9" icon={Compass} label="Mise en perspective" color="#9333ea" testid="section-9">
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">Ce que l&apos;élève va travailler d&apos;ici la prochaine séance</Label>
            <Textarea rows={3} value={form.travailProchaineSeance} onChange={(e) => update('travailProchaineSeance', e.target.value)} data-testid="f-travail-suivant" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">Conseils de l&apos;enseignant</Label>
            <Textarea rows={3} value={form.conseilsEnseignant} onChange={(e) => update('conseilsEnseignant', e.target.value)} data-testid="f-conseils" />
          </div>
        </div>
      </EvalSection>

      {/* SECTION 10 — BILAN FIN DE SÉANCE */}
      <EvalSection n="10" icon={Award} label="Bilan de fin de séance" duree="5 min" color="#1d4ed8" testid="section-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">Points acquis</Label>
            <Textarea rows={3} value={form.bilanPointsAcquis} onChange={(e) => update('bilanPointsAcquis', e.target.value)} data-testid="f-acquis" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">Points à améliorer</Label>
            <Textarea rows={3} value={form.bilanPointsAAmeliorer} onChange={(e) => update('bilanPointsAAmeliorer', e.target.value)} data-testid="f-bilan-ameliorer" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-black block mb-1">Travail prévu prochaine séance</Label>
            <Textarea rows={3} value={form.bilanTravailPrevu} onChange={(e) => update('bilanTravailPrevu', e.target.value)} data-testid="f-travail-prevu" />
          </div>
        </div>
      </EvalSection>

      {/* RÉCAP VALIDATIONS REMC — visible écran + PDF */}
      <LivretValidationsRecap
        validations={form.livretValidations}
        accent="#7c3aed"
        studentName={form.nomEleve}
        context="circulation"
      />

      {/* SIGNATURES */}
      <section className="rounded-2xl bg-card border border-border/60 p-5 sm:p-6" data-testid="signatures">
        <h3 className="text-sm font-black tracking-tight mb-3 flex items-center gap-2">
          <Save className="w-4 h-4 text-[#7c3aed]" /> Signatures
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {['Signature élève', 'Signature enseignant'].map((label) => (
            <div key={label}>
              <Label className="text-[10px] uppercase tracking-wider font-black">{label}</Label>
              <div className="mt-1 h-16 rounded-xl border-2 border-dashed border-border bg-muted/30 flex items-center justify-center text-[10px] text-muted-foreground italic">
                À signer après impression
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="flex flex-wrap gap-2 no-print">
        <Button onClick={() => window.print()} className="gap-1.5 bg-[#7c3aed]" data-testid="bottom-print">
          <Printer className="w-4 h-4" /> Imprimer / PDF
        </Button>
        <Button variant="outline" onClick={() => { if (window.confirm("Vider la fiche ?")) setForm(EMPTY); }} className="gap-1.5">
          <Trash2 className="w-4 h-4" /> Vider la fiche
        </Button>
      </div>
    </div>
  );
}
